import React, { useState } from "react";
import { Divider } from "@mui/material";

// Components
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import BackOrderBadge from "@/main/components/shared/backOrderBadge/backOrderBadge";
import FavoriteButton from "@/main/components/shared/favoriteButton/favoriteButton";
import QuoteProcurementPartInfo, {
	getTooltipFormatted,
} from "@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfo";
import PriceDiffPercent from "@/main/components/shared/priceDiffPercent/priceDiffPercent";
import TypographyPriceCap from "@/main/components/common/typography/typographyPriceCap";
import KOGImage from "@/main/components/shared/kogImage/kogImage";
import KOGCollapse from "@/main/components/shared/kogCollapse/kogCollapse";
import { SpinnerLoading } from "@/main/components/shared/styles";

// Styles
import * as S from "./styles";

// Utils
import {
	getTranslateByKey,
	isShowControlByElementId,
	useIsShowControlByElementId,
	isNullUndefined,
	isNullUndefinedEmpty,
} from "@/main/services/utils";
import { formatCurrency } from "@/main/services/utilsNumber";
import { Controls } from "@/main/services/constants/constantsControl";

// Redux & context
import { Cart, CartPart, CartState, QuotationShipingCompanyItems, removeCart, useCartReducer } from "@/main/store/cart";
import { userIsAdmin, userIsSupplier } from "@/main/services/utilsUser";
import ButtonDeletePart from "./components/buttonDeletePart";
import ButtonCorrectPrice from "./components/buttonCorrectPrice";
import { canCorrectCartPrice } from "./components/utilsCorrectPrice";
import StarRatingPartially from "@/main/components/shared/starRating/starRatingPartially";
import { PartTypeEnum } from "@/main/services/enum/PartTypeEnum";
import { AddCartType, RefreshQuoteByGetItemsType } from "./components/types";

// API
import { quoteApi } from "@/main/api";

// Modal
import KogModalSideComponent from "../../modal/ModalSide";
import ModalPartDetails from "@/main/screens/order/createorder/steps/stepTwo/modalPartDetails/modalPartDetails";

// Types
import { QuoteSelectSuppliersDataResponse, QuoteSelectSuppliersDataResponseItems } from "@/main/models/response";
import RowSupplierAssigned from "./components/rowSupplierAssigned/rowSupplierAssigned";
import { ProcurementStatusEnum } from "@/main/services/enum/StatusProcurement";
import { formatAddCart } from "./components/utilsBoxCart";
import { useDispatch } from "react-redux";

interface IBoxCartProps {
	cart: Cart;
	isQuoteCanceled?: boolean;
	isPaymentPending: boolean;
	isQuoteBlocked: boolean;
	showCompareSuppliers: boolean;
	canShowDeleteBoutton: boolean;
	guidQuote: string;
	guidScreen: string;
	dataCart: CartState;
	addCart: AddCartType;
	integration: boolean;
	idProcurementStatus: number;
	refreshQuoteByGetItems: RefreshQuoteByGetItemsType;
	displayCompareSuppliers: (
		value: string,
		externalPartId: string | null,
		event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => void;
}
interface IDetailSupplier {
	guid: string | null;
	supplierInventoryGuid: boolean;
}

const INITIAL_DETAIL_SUPPLIER = {
	guid: null,
	supplierInventoryGuid: false,
};

function BoxCart(props: IBoxCartProps) {
	const {
		cart,
		showCompareSuppliers,
		canShowDeleteBoutton,
		isPaymentPending,
		guidQuote,
		guidScreen,
		dataCart,
		integration,
		idProcurementStatus,
		refreshQuoteByGetItems,
		displayCompareSuppliers,
		isQuoteBlocked,
		addCart,
	} = props;

	const [openModalDetails, setOpenModalDetails] = useState(false);
	const [detailSupplier, setDetailSupplier] = useState<IDetailSupplier>(INITIAL_DETAIL_SUPPLIER);

	const cartReducer = useCartReducer();

	function renderShippingCompanies(cart: Cart) {
		if (cart.shipingIntegrationIsUnavailable) {
			return (
				<div className="d-flex">{getTranslateByKey("information.unavailableErrorConnectionToShippingCompany")}</div>
			);
		}
		if (cart.deliveryBySupplier) {
			const daysDelivery = cart.deliveryDaysSupplier || 0;
			const message = `
      ${getTranslateByKey("information.supplierDeliver")} -
      ${getTranslateByKey("information.aboutWorkDays").replace("{0}", String(daysDelivery))}`;

			return (
				<>
					<h6 style={{ fontWeight: "bold" }}>{message}</h6>
					<S.TextFreeShipping>{getTranslateByKey("information.shippingIncluded")}</S.TextFreeShipping>
				</>
			);
		}

		if (
			cart.shipingCompany &&
			!cart.shipingCompany.errorMessage &&
			cart.shipingCompany.quotationShipingCompanyItems &&
			cart.shipingCompany.quotationShipingCompanyItems.length > 0
		) {
			const showShippingCostPrice = userIsAdmin() && !cart.deliveryBySupplier;
			const messageShipping = showShippingCostPrice
				? `${getTranslateByKey("information.shippingIncluded")} (${formatCurrency(cart.totalShippingCost)})`
				: getTranslateByKey("information.shippingIncluded");
			// Currently, there will only be one item in this list (the only carrier so far is PaquetExpress).
			return (
				<>
					{renderItemsShippingCompanies(cart.shipingCompany.quotationShipingCompanyItems)}

					<S.TextFreeShipping>{messageShipping}</S.TextFreeShipping>
				</>
			);
		}

		return <></>;
	}
	function renderItemsShippingCompanies(quotationShipingCompanyItems: QuotationShipingCompanyItems[]) {
		return quotationShipingCompanyItems.map(
			({ deliveryDays, shippingCompany, serviceName }: QuotationShipingCompanyItems, index: number) => {
				const message = `${shippingCompany} - ${serviceName} - ${getTranslateByKey("information.aboutWorkDays").replace(
					"{0}",
					deliveryDays,
				)}`;

				return (
					<h6 key={index} style={{ fontWeight: "bold" }}>
						{message}
					</h6>
				);
			},
		);
	}

	const showPrices = isShowControlByElementId(Controls.IdShowPrices);
	const hasCorrectPriceControl = useIsShowControlByElementId(Controls.IdCorrectPriceAwaitingRelease);

	const [isLoadingParts, setIsLoadingParts] = useState<Record<string, boolean>>({});
	const [dataParts, setDataParts] = useState<Record<string, QuoteSelectSuppliersDataResponseItems | null>>({});

	function fetchPartsAssigned(isCollapsed: boolean, part: CartPart) {
		const key = String(part.guidQuoteItem);

		if (!isCollapsed) return;
		if (isLoadingParts[key]) return;
		if (!isNullUndefined(dataParts[key])) return;

		setIsLoadingParts((prev) => ({
			...prev,
			[key]: true,
		}));

		quoteApi
			.getSupplierItems(key)
			.then((response) => {
				const {
					data: { result },
				} = response;

				setDataParts((prev) => ({
					...prev,
					[key]: result,
				}));
			})
			.catch(() => {})
			.finally(() => {
				setIsLoadingParts((prev) => ({
					...prev,
					[key]: false,
				}));
			});
	}

	const isQuoteOpen = idProcurementStatus === ProcurementStatusEnum.Open;
	const isQuoteCanceled = idProcurementStatus === ProcurementStatusEnum.Cancelled;
	const isQuoteManual =
		idProcurementStatus === ProcurementStatusEnum.Manual ||
		idProcurementStatus === ProcurementStatusEnum.WaitingRelease;
	const canUseCollapse = idProcurementStatus === ProcurementStatusEnum.Manual;
	const isUserSupplier = userIsSupplier();
	const isAwaitingReleaseSupplier = isUserSupplier && idProcurementStatus === ProcurementStatusEnum.WaitingRelease;
	const canCorrectPrice = canCorrectCartPrice({
		idProcurementStatus,
		isUserSupplier,
		showPrices,
		hasCorrectPriceControl,
	});

	const isPaintProject = (idPartType: number) => idPartType === PartTypeEnum.Paint;
	const showDeletePartButton = canShowDeleteBoutton && !isPaymentPending;

	const dispatch = useDispatch();
	function handleReplaceSupplier(currentPart: CartPart, newSupplierItem: QuoteSelectSuppliersDataResponse) {
		const key = String(currentPart.guidQuoteItem);

		dispatch(removeCart(String(currentPart.guidQuoteSupplierItem), currentPart.guidPart, false, false, []));

		const { formattedPart, formattedSupplierCart } = formatAddCart({
			externalPartId: currentPart.externalPartId,
			guidPart: currentPart.guidPart,
			guidQuote,
			partItem: newSupplierItem,
		});

		addCart(formattedSupplierCart, formattedPart.guidQuoteSupplierItem);

		setDataParts((prev) => ({
			...prev,
			[key]: null,
		}));
	}

	return (
		<S.ContainerBox>
			<S.HeaderBox>
				<S.ContainerTitleBox>
					<S.TitleBox>{cart.name}</S.TitleBox>
					<FavoriteButton isFavorite={cart.isFavoriteSupplier} notClickable onToggleFavorite={() => {}} />
				</S.ContainerTitleBox>
				<StarRatingPartially rating={cart.rating ?? 0} />
			</S.HeaderBox>

			{cart.parts.map((part: CartPart) =>
				canUseCollapse ? (
					<KOGCollapse
						key={part.guid}
						customIcon={
							isLoadingParts[String(part.guidQuoteItem)] ? <SpinnerLoading noMargin size="xsmall" /> : undefined
						}
						onCustomCollapse={(isCollapsed) => fetchPartsAssigned(isCollapsed, part)}
						isOpenCollapseinitial={false}
						titleTooltipIcon={{
							showLess: getTranslateByKey("information.showLess"),
							showMore: getTranslateByKey("information.clicktoseetheofers"),
						}}
						header={
							<S.BodyBox>
								<S.ContainerPart>
									<KOGImage width={100} height={100} src={part.image} />

									<S.ColumnPart>
										<S.PartInfo title={getTooltipFormatted(part.quoteProcurementPartInfo)}>
											<QuoteProcurementPartInfo
												isTooltip
												data={part.quoteProcurementPartInfo}
												isEllipsisText={false}
												renderType="column"
												showLineSupplierInfo={{ info: "all" }}
												showIconNotes
											/>
										</S.PartInfo>
										<S.Quantity>{part.quantity}</S.Quantity>
									</S.ColumnPart>

									{showCompareSuppliers && idProcurementStatus !== ProcurementStatusEnum.Manual && (
										<SecondaryButton
											style={{ padding: ".2rem .8rem" }}
											id="id-summary-button-compareSuppliers"
											onClick={(event) => displayCompareSuppliers(part.guidPart, part.externalPartId, event)}
											disabled={isQuoteCanceled}
										>
											{getTranslateByKey("information.compareSuppliers")}
										</SecondaryButton>
									)}

									<S.ContainerPartPrice>
										<h6>
											{part.backOrder && <BackOrderBadge />} {showPrices && formatCurrency(part.price)}
											{showPrices &&
												!isUserSupplier &&
												!isNullUndefined(part.priceDiffPercent) &&
												!isPaintProject(part.idPartType) && <PriceDiffPercent diffPercent={part.priceDiffPercent} />}
										</h6>
										{showPrices &&
											!isAwaitingReleaseSupplier &&
											!isPaintProject(part.idPartType) &&
											(isNullUndefinedEmpty(part.systemReferencePriceFormatted) ? (
												<TypographyPriceCap priceCap={part.priceCapFormatted} />
											) : (
												<p style={{ margin: "0" }} translate="no">
													{getTranslateByKey("information.systemReferencePrice")}: {part.systemReferencePriceFormatted}
												</p>
											))}
									</S.ContainerPartPrice>
								</S.ContainerPart>

								<S.AreaButtonAbsolute>
									{showDeletePartButton && (
										<ButtonDeletePart
											type="assigned"
											dataCart={dataCart}
											refreshQuoteByGetItems={refreshQuoteByGetItems}
											isPaymentPending={isPaymentPending}
											isQuoteBlocked={isQuoteBlocked}
											isQuoteCanceled={isQuoteCanceled}
											guidQuote={guidQuote}
											guidQuoteItem={String(part.guid)}
											idButtonDeletePart={`id-createOrder-button-deleteProduct-${part.guid}`}
											partName={part.quoteProcurementPartInfo?.quoteItemDescription}
											callbackAfterRemovePart={() => {}}
											guidQuoteSupplierItem={String(part.guidQuoteSupplierItem)}
										/>
									)}
								</S.AreaButtonAbsolute>
							</S.BodyBox>
						}
						content={
							cartReducer.cartItems.isLoadingUpdate ? (
								<SpinnerLoading size="medium" />
							) : (
								<RowSupplierAssigned
									addCart={addCart}
									dataCart={dataCart}
									integration={integration}
									isQuoteManual={isQuoteManual}
									isQuoteOpen={isQuoteOpen}
									dataParts={dataParts[String(part.guidQuoteItem)]}
									isLoading={isLoadingParts[String(part.guidQuoteItem)]}
									externalPartId={part.externalPartId}
									guidPart={part.guidPart}
									guidQuote={String(guidQuote)}
									onReplaceSupplier={(supplier) => handleReplaceSupplier(part, supplier)}
								/>
							)
						}
					/>
				) : (
					<S.BodyBox key={part.guid}>
						<S.ContainerPart>
							<KOGImage width={100} height={100} src={part.image} />

							<S.ColumnPart>
								<S.PartInfo title={getTooltipFormatted(part.quoteProcurementPartInfo)}>
									<QuoteProcurementPartInfo
										data={part.quoteProcurementPartInfo}
										renderType="column"
										showLineSupplierInfo={{ info: "all" }}
										showIconNotes
									/>
								</S.PartInfo>
								<S.Quantity>{part.quantity}</S.Quantity>
							</S.ColumnPart>

							<S.AreaButtonAbsolute>
								{showDeletePartButton && (
									<ButtonDeletePart
										type="assigned"
										dataCart={dataCart}
										refreshQuoteByGetItems={refreshQuoteByGetItems}
										isPaymentPending={isPaymentPending}
										isQuoteBlocked={isQuoteBlocked}
										isQuoteCanceled={isQuoteCanceled}
										guidQuote={guidQuote}
										guidQuoteItem={String(part.guid)}
										idButtonDeletePart={`id-createOrder-button-deleteProduct-${part.guid}`}
										partName={part.quoteProcurementPartInfo?.quoteItemDescription}
										callbackAfterRemovePart={() => {}}
										guidQuoteSupplierItem={String(part.guidQuoteSupplierItem)}
									/>
								)}
							</S.AreaButtonAbsolute>

							<S.ContainerPartPrice>
								<h6>
									{part.backOrder && <BackOrderBadge />}{" "}
									{canCorrectPrice && (
										<ButtonCorrectPrice
											part={part}
											supplierName={cart.name}
											guidScreen={guidScreen}
											idButtonCorrectPrice={`id-boxCart-button-correctPrice-${part.guidQuoteSupplierItem}`}
											refreshQuoteByGetItems={refreshQuoteByGetItems}
										/>
									)}
									{showPrices && formatCurrency(part.price)}
									{showPrices &&
										!isUserSupplier &&
										!isNullUndefined(part.priceDiffPercent) &&
										!isPaintProject(part.idPartType) && <PriceDiffPercent diffPercent={part.priceDiffPercent} />}
								</h6>
								{showPrices && !isAwaitingReleaseSupplier && <TypographyPriceCap priceCap={part.priceCapFormatted} />}
							</S.ContainerPartPrice>
						</S.ContainerPart>
					</S.BodyBox>
				),
			)}
			<Divider />
			<S.ContainerDelivery>
				<S.IconShipping />
				<h6 style={{ marginLeft: "0.5rem", fontWeight: "bold" }}>{getTranslateByKey("information.deliveryType")}</h6>
			</S.ContainerDelivery>
			<S.ContainerDeliveryPrice>
				<S.ContainerInfoDelivery>{renderShippingCompanies(cart)}</S.ContainerInfoDelivery>
				{showPrices && (
					<h6 style={{ fontWeight: "bold" }}>
						{`${getTranslateByKey("information.total")}: ${formatCurrency(cart.totalPartsPrice)}`}
					</h6>
				)}
			</S.ContainerDeliveryPrice>

			<KogModalSideComponent
				open={openModalDetails}
				closeModal={() => {
					setOpenModalDetails(false);
					setDetailSupplier(INITIAL_DETAIL_SUPPLIER);
				}}
			>
				{openModalDetails && (
					<ModalPartDetails
						closeModal={() => {
							setOpenModalDetails(false);
							setDetailSupplier(INITIAL_DETAIL_SUPPLIER);
						}}
						guid={detailSupplier.guid}
						quoteSupplierItem={detailSupplier.supplierInventoryGuid}
					/>
				)}
			</KogModalSideComponent>
		</S.ContainerBox>
	);
}

export default BoxCart;
